import { type ReactNode, useId } from 'react'

/* ==========================================================================
   FormField Component
   Based on design.md specifications
   ========================================================================== */

export interface FormFieldProps {
  /** Field label text */
  label: string
  /** Input element(s) to render */
  children: ReactNode
  /** Helper text displayed below input */
  helperText?: string
  /** Error message (overrides helperText when present) */
  error?: string
  /** Mark field as required (shows red asterisk) */
  required?: boolean
  /** Custom id for label association (auto-generated if not provided) */
  htmlFor?: string
  /** Additional CSS classes for the container */
  className?: string
}

/**
 * Form field wrapper with label, input slot, and helper/error text.
 * Handles label association and required field indicators.
 *
 * @example
 * ```tsx
 * <FormField label="Email" required>
 *   <Input type="email" />
 * </FormField>
 *
 * <FormField label="Name" helperText="As it appears on your ID">
 *   <Input />
 * </FormField>
 *
 * <FormField label="Password" error="Password is required">
 *   <Input type="password" error />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  children,
  helperText,
  error,
  required = false,
  htmlFor,
  className = '',
}: FormFieldProps) {
  const generatedId = useId()
  const fieldId = htmlFor ?? generatedId
  const hasError = Boolean(error)
  const descriptionId = `${fieldId}-description`

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* Label */}
      <label htmlFor={fieldId} className="text-sm font-medium text-gray-700">
        {label}
        {required && (
          <span className="text-error ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {/* Input slot */}
      {children}

      {/* Helper or error text */}
      {(helperText || error) && (
        <p
          id={descriptionId}
          className={`text-sm ${hasError ? 'text-error' : 'text-gray-600'}`}
          role={hasError ? 'alert' : undefined}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  )
}

export default FormField
